import * as tslib_1 from "tslib";
import * as d3 from 'd3-selection';
import stringify from 'json-stringify-pretty-compact';
import { satisfies } from 'semver';
import * as vegaImport from 'vega-lib';
import * as vlImport from 'vega-lite';
import schemaParser from 'vega-schema-url-parser';
import * as themes from 'vega-themes';
import { Handler } from 'vega-tooltip';
import { post } from './post';
import embedStyle from './style';
import { mergeDeep } from './util';
export var vega = vegaImport;
export var vl = vlImport;
var NAMES = {
    vega: 'Vega',
    'vega-lite': 'Vega-Lite',
};
var VERSION = {
    vega: vega.version,
    'vega-lite': vl ? vl.version : 'not available',
};
var PREPROCESSOR = {
    vega: function (vgjson, _) { return vgjson; },
    'vega-lite': function (vljson, config) { return vl.compile(vljson, { config: config }).spec; },
};
var SVG_CIRCLES = "\n<svg viewBox=\"0 0 16 16\" fill=\"currentColor\" stroke=\"none\" stroke-width=\"1\" stroke-linecap=\"round\" stroke-linejoin=\"round\" width=\"14\" height=\"14\">\n  <circle r=\"2\" cy=\"8\" cx=\"2\"></circle>\n  <circle r=\"2\" cy=\"8\" cx=\"8\"></circle>\n  <circle r=\"2\" cy=\"8\" cx=\"14\"></circle>\n</svg>";
var I18N = {
    CLICK_TO_VIEW_ACTIONS: 'Click to view actions',
    COMPILED_ACTION: 'View Vega',
    EDITOR_ACTION: 'Open in Vega Editor',
    PNG_ACTION: 'Save as PNG',
    SOURCE_ACTION: 'View Source',
    SVG_ACTION: 'Save as SVG',
};
function isTooltipHandler(h) {
    return typeof h === 'function';
}
function viewSource(source, sourceHeader, sourceFooter, mode) {
    var header = "<html><head>" + sourceHeader + "</head><body><pre><code class=\"json\">";
    var footer = "</code></pre>" + sourceFooter + "</body></html>";
    var win = window.open('');
    win.document.write(header + source + footer);
    win.document.title = NAMES[mode] + " JSON Source";
}
/**
 * Try to guess the type of spec.
 *
 * @param spec Vega or Vega-Lite spec.
 */
export function guessMode(spec, providedMode) {
    // Decide mode
    if (spec.$schema) {
        var parsed = schemaParser(spec.$schema);
        if (providedMode && providedMode !== parsed.library) {
            console.warn("The given visualization spec is written in " + NAMES[parsed.library] + ", but mode argument sets " + (NAMES[providedMode] || providedMode) + ".");
        }
        var mode = parsed.library;
        if (!satisfies(VERSION[mode], "^" + parsed.version.slice(1))) {
            console.warn("The input spec uses " + mode + " " + parsed.version + ", but the current version of " + NAMES[mode] + " is " + VERSION[mode] + ".");
        }
        return mode;
    }
    else {
        // try to guess from the provided spec
        if ('mark' in spec ||
            'encoding' in spec ||
            'layer' in spec ||
            'hconcat' in spec ||
            'vconcat' in spec ||
            'facet' in spec ||
            'repeat' in spec) {
            return 'vega-lite';
        }
        if ('marks' in spec || 'signals' in spec || 'scales' in spec || 'axes' in spec) {
            return 'vega';
        }
    }
    return providedMode || 'vega';
}
function isLoader(o) {
    return !!(o && 'load' in o);
}
/**
 * Embed a Vega visualization component in a web page. This function returns a promise.
 *
 * @param el        DOM element in which to place component (DOM node or CSS selector).
 * @param spec      String : A URL string from which to load the Vega specification.
 *                  Object : The Vega/Vega-Lite specification as a parsed JSON object.
 * @param opt       A JavaScript object containing options for embedding.
 */
export default function embed(el, spec, opt) {
    if (opt === void 0) { opt = {}; }
    return tslib_1.__awaiter(this, void 0, void 0, function () {
        var patch, actions, i18n, loader, renderer, logLevel, data, config, data, ID, style, mode, vgSpec, parsed, div, runtime, view, handler, hover, _a, hoverSet, updateSet, wrapper, details, summary, dn_1, ctrl, _loop_1, _i, _b, ext, editorUrl_1;
        return tslib_1.__generator(this, function (_c) {
            switch (_c.label) {
                case 0:
                    opt = opt || {};
                    patch = opt.patch || opt.onBeforeParse;
                    actions = opt.actions === true || opt.actions === false
                        ? opt.actions
                        : mergeDeep({}, { export: { svg: true, png: true }, source: true, compiled: false, editor: true }, opt.actions || {});
                    i18n = tslib_1.__assign({}, I18N, opt.i18n);
                    loader = isLoader(opt.loader) ? opt.loader : vega.loader(opt.loader);
                    renderer = opt.renderer || 'canvas';
                    logLevel = opt.logLevel || vega.Warn;
                    if (!vega.isString(spec)) return [3 /*break*/, 2];
                    return [4 /*yield*/, loader.load(spec)];
                case 1:
                    data = _c.sent();
                    return [2 /*return*/, embed(el, JSON.parse(data), opt)];
                case 2:
                    config = opt.config || {};
                    if (!vega.isString(config)) return [3 /*break*/, 4];
                    return [4 /*yield*/, loader.load(config)];
                case 3:
                    data = _c.sent();
                    return [2 /*return*/, embed(el, spec, tslib_1.__assign({}, opt, { config: JSON.parse(data) }))];
                case 4:
                    if (opt.defaultStyle) {
                        ID = 'vega-embed-style';
                        if (!document.getElementById(ID)) {
                            style = document.createElement('style');
                            style.id = ID;
                            style.innerText = opt.defaultStyle === true ? (embedStyle || '').toString() : opt.defaultStyle;
                            document.getElementsByTagName('head')[0].appendChild(style);
                        }
                    }
                    if (opt.theme) {
                        config = mergeDeep({}, themes[opt.theme], config);
                    }
                    mode = guessMode(spec, opt.mode);
                    vgSpec = PREPROCESSOR[mode](spec, config);
                    if (mode === 'vega-lite') {
                        if (vgSpec.$schema) {
                            parsed = schemaParser(vgSpec.$schema);
                            if (!satisfies(VERSION.vega, "^" + parsed.version.slice(1))) {
                                console.warn("The compiled spec uses Vega " + parsed.version + ", but current version is " + VERSION.vega + ".");
                            }
                        }
                    }
                    div = d3
                        .select(el) // d3.select supports elements and strings
                        .classed('vega-embed', true)
                        .html('');
                    if (patch) {
                        if (patch instanceof Function) {
                            vgSpec = patch(vgSpec);
                        }
                        else {
                            vgSpec = mergeDeep(vgSpec, patch);
                        }
                    }
                    runtime = vega.parse(vgSpec, mode === 'vega-lite' ? {} : config);
                    view = new vega.View(runtime, {
                        loader: loader,
                        logLevel: logLevel,
                        renderer: renderer,
                    }).initialize(el);
                    if (opt.tooltip !== false) {
                        handler = void 0;
                        if (isTooltipHandler(opt.tooltip)) {
                            handler = opt.tooltip;
                        }
                        else {
                            // user provided boolean true or tooltip options
                            handler = new Handler(opt.tooltip === true ? {} : opt.tooltip).call;
                        }
                        view.tooltip(handler);
                    }
                    hover = opt.hover;
                    // Enable hover for Vega by default.
                    if (hover === undefined) {
                        hover = mode !== 'vega-lite';
                    }
                    if (hover) {
                        _a = (typeof hover === 'boolean' ? {} : hover), hoverSet = _a.hoverSet, updateSet = _a.updateSet;
                        view.hover(hoverSet, updateSet);
                    }
                    if (opt) {
                        if (opt.width) {
                            view.width(opt.width);
                        }
                        if (opt.height) {
                            view.height(opt.height);
                        }
                        if (opt.padding) {
                            view.padding(opt.padding);
                        }
                    }
                    if (!opt.runAsync) return [3 /*break*/, 6];
                    return [4 /*yield*/, view.runAsync()];
                case 5:
                    _c.sent();
                    return [3 /*break*/, 7];
                case 6:
                    view.run();
                    _c.label = 7;
                case 7:
                    if (actions !== false) {
                        wrapper = div;
                        if (opt.defaultStyle === true) {
                            details = (wrapper = div.append('details').attr('title', I18N.CLICK_TO_VIEW_ACTIONS));
                            summary = details.insert('summary');
                            summary.html(SVG_CIRCLES);
                            dn_1 = details.node();
                            document.addEventListener('click', function (evt) {
                                if (!dn_1.contains(evt.target)) {
                                    dn_1.removeAttribute('open');
                                }
                            });
                        }
                        ctrl = wrapper.insert('div').attr('class', 'vega-actions');
                        // add 'Export' action
                        if (actions === true || actions.export !== false) {
                            _loop_1 = function (ext) {
                                if (actions === true || actions.export === true || actions.export[ext]) {
                                    var i18nExportAction = i18n[ext.toUpperCase() + "_ACTION"];
                                    ctrl
                                        .append('a')
                                        .text(i18nExportAction)
                                        .attr('href', '#')
                                        .attr('target', '_blank')
                                        .attr('download', "visualization." + ext)
                                        .on('mousedown', function () {
                                        var _this = this;
                                        view
                                            .toImageURL(ext, opt.scaleFactor)
                                            .then(function (url) {
                                            _this.href = url;
                                        })
                                            .catch(function (error) {
                                            throw error;
                                        });
                                        d3.event.preventDefault();
                                    });
                                }
                            };
                            for (_i = 0, _b = ['svg', 'png']; _i < _b.length; _i++) {
                                ext = _b[_i];
                                _loop_1(ext);
                            }
                        }
                        // add 'View Source' action
                        if (actions === true || actions.source !== false) {
                            ctrl
                                .append('a')
                                .text(i18n.SOURCE_ACTION)
                                .attr('href', '#')
                                .on('mousedown', function () {
                                viewSource(stringify(spec), opt.sourceHeader || '', opt.sourceFooter || '', mode);
                                d3.event.preventDefault();
                            });
                        }
                        // add 'View Compiled' action
                        if (mode === 'vega-lite' && (actions === true || actions.compiled !== false)) {
                            ctrl
                                .append('a')
                                .text(i18n.COMPILED_ACTION)
                                .attr('href', '#')
                                .on('mousedown', function () {
                                viewSource(stringify(vgSpec), opt.sourceHeader || '', opt.sourceFooter || '', 'vega');
                                d3.event.preventDefault();
                            });
                        }
                        // add 'Open in Vega Editor' action
                        if (actions === true || actions.editor !== false) {
                            editorUrl_1 = opt.editorUrl || 'https://vega.github.io/editor/';
                            ctrl
                                .append('a')
                                .text(i18n.EDITOR_ACTION)
                                .attr('href', '#')
                                .on('mousedown', function () {
                                post(window, editorUrl_1, {
                                    config: config,
                                    mode: mode,
                                    renderer: renderer,
                                    spec: stringify(spec),
                                });
                                d3.event.preventDefault();
                            });
                        }
                    }
                    return [2 /*return*/, { view: view, spec: spec, vgSpec: vgSpec }];
            }
        });
    });
}
//# sourceMappingURL=embed.js.map